import { prepareContent } from '@/lib/message';
import { isEqual } from 'lodash';
import { forwardRef, memo, useMemo } from 'react';

import type { IMessageElement, IStep } from '@chainlit/react-client';

import { CURSOR_PLACEHOLDER } from '@/components/BlinkingCursor';
import { Markdown } from '@/components/Markdown';

import { InlinedElements } from './InlinedElements';

type ContentSection = 'input' | 'output';

export interface Props {
  elements: IMessageElement[];
  message: IStep;
  allowHtml?: boolean;
  latex?: boolean;
  renderMarkdown?: boolean;
  sections?: ContentSection[];
}

const getMessageRenderProps = (message: IStep) => ({
  id: message.id,
  output: message.output,
  input: message.input,
  language: message.language,
  streaming: message.streaming,
  showInput: message.showInput,
  type: message.type
});

/**
 * Split a streaming markdown body at the last "safe" paragraph boundary so
 * we can render the stable prefix once and re-parse only the short tail per
 * token. Caps per-render markdown work at O(tail) instead of O(full body),
 * which is the difference between linear and quadratic total work over a
 * long streaming reply.
 *
 * "Safe" means the split point is outside any open fenced code block (```)
 * and outside any open block-math fence ($$). Tables, lists, and inline
 * spans are split-safe at \n\n.
 */
const splitForStreaming = (
  content: string
): { prefix: string; tail: string } => {
  let inFence = false;
  let inMath = false;
  let lastSafe = -1;
  const len = content.length;
  let i = 0;
  while (i < len) {
    const c = content.charCodeAt(i);
    // ``` toggle (only counted when not inside $$)
    if (
      !inMath &&
      c === 96 /* ` */ &&
      content.charCodeAt(i + 1) === 96 &&
      content.charCodeAt(i + 2) === 96
    ) {
      inFence = !inFence;
      i += 3;
      continue;
    }
    // $$ toggle (only counted when not inside ```)
    if (
      !inFence &&
      c === 36 /* $ */ &&
      content.charCodeAt(i + 1) === 36
    ) {
      inMath = !inMath;
      i += 2;
      continue;
    }
    // \n\n boundary, only safe outside fences
    if (
      !inFence &&
      !inMath &&
      c === 10 /* \n */ &&
      content.charCodeAt(i + 1) === 10
    ) {
      // Skip any additional consecutive newlines so the prefix consumes them.
      let j = i + 2;
      while (j < len && content.charCodeAt(j) === 10) j++;
      lastSafe = j;
      i = j;
      continue;
    }
    i++;
  }
  if (lastSafe <= 0 || lastSafe >= len) {
    return { prefix: '', tail: content };
  }
  return {
    prefix: content.slice(0, lastSafe),
    tail: content.slice(lastSafe)
  };
};

const MessageContent = memo(
  forwardRef<HTMLDivElement, Props>(
    (
      { message, elements, allowHtml, latex, renderMarkdown, sections },
      ref
    ) => {
      // Run prepareContent on the raw body (no cursor placeholder). The cursor
      // is appended to the tail chunk below so it doesn't end up frozen inside
      // the memoized prefix when streaming.
      const {
        preparedContent: output,
        inlinedElements: outputInlinedElements,
        refElements: outputRefElements
      } = prepareContent({
        elements,
        id: message.id,
        content: message.output,
        language: message.language
      });

      // Chunked streaming render: split the prepared body at the last safe
      // paragraph boundary so the long stable prefix is re-parsed at most once
      // (when a new paragraph completes) and only the short tail re-parses
      // each token. This is the difference between linear and quadratic total
      // markdown work over a long streaming reply.
      const { prefix: outputPrefix, tail: outputTail } = useMemo(
        () =>
          message.streaming && output ? splitForStreaming(output) : { prefix: '', tail: output },
        [output, message.streaming]
      );
      const outputTailWithCursor =
        message.streaming && outputTail ? outputTail + CURSOR_PLACEHOLDER : outputTail;

      const selectedSections = sections ?? ['input', 'output'];
      const sectionsSet = useMemo(
        () => new Set(selectedSections),
        [selectedSections]
      );

      const displayInput =
        sectionsSet.has('input') && message.input && message.showInput;
      const displayOutput = sectionsSet.has('output');

      const isMessage = message.type.includes('message');

      const outputMarkdown = displayOutput ? (
        <>
          {!isMessage && displayInput && message.output ? (
            <div className="font-medium">Output</div>
          ) : null}
          {outputPrefix ? (
            <Markdown
              allowHtml={allowHtml}
              latex={latex}
              renderMarkdown={renderMarkdown}
              refElements={outputRefElements}
            >
              {outputPrefix}
            </Markdown>
          ) : null}
          {outputTailWithCursor ? (
            <Markdown
              allowHtml={allowHtml}
              latex={latex}
              renderMarkdown={renderMarkdown}
              refElements={outputRefElements}
            >
              {outputTailWithCursor}
            </Markdown>
          ) : null}
        </>
      ) : null;

      let inputMarkdown;

      if (displayInput) {
        const inputContent =
          message.streaming && message.input
            ? message.input + CURSOR_PLACEHOLDER
            : message.input;
        const { preparedContent: input, refElements: inputRefElements } =
          prepareContent({
            elements,
            id: message.id,
            content: inputContent,
            language:
              typeof message.showInput === 'string'
                ? message.showInput
                : undefined
          });

        inputMarkdown = (
          <>
            <Markdown
              allowHtml={allowHtml}
              latex={latex}
              renderMarkdown={renderMarkdown}
              refElements={inputRefElements}
            >
              {input}
            </Markdown>
          </>
        );
      }

      const markdownContent = (
        <div className="flex flex-col gap-4">
          {inputMarkdown}
          {outputMarkdown}
        </div>
      );

      return (
        <div ref={ref} className="message-content w-full flex flex-col gap-2">
          {displayInput || (displayOutput && output) ? markdownContent : null}
          {displayOutput ? (
            <InlinedElements elements={outputInlinedElements} />
          ) : null}
        </div>
      );
    }
  ),
  (prevProps, nextProps) => {
    return (
      prevProps.allowHtml === nextProps.allowHtml &&
      prevProps.latex === nextProps.latex &&
      prevProps.renderMarkdown === nextProps.renderMarkdown &&
      prevProps.elements === nextProps.elements &&
      isEqual(
        prevProps.sections ?? ['input', 'output'],
        nextProps.sections ?? ['input', 'output']
      ) &&
      isEqual(
        getMessageRenderProps(prevProps.message),
        getMessageRenderProps(nextProps.message)
      )
    );
  }
);

export { MessageContent };
